/**
 * The panel for a value the server will never say again.
 *
 * Three things on this page — an invite token, an admin-issued temporary
 * password, and the URL built from the token — exist in readable form for
 * exactly one HTTP response. The backend stores a SHA-256 hash of the invite
 * and an Argon2 hash of the password, so "I'll copy it later" is not a thing
 * that can be honoured: later, there is nothing to copy and the only remedy is
 * to issue a new one and invalidate the old.
 *
 * That is why the warning is `role="alert"` and rendered before the value
 * rather than as a footnote, why the panel never dismisses itself on a re-render
 * or a background refetch, and why a failed clipboard write says so out loud
 * instead of leaving a button that looks like it worked.
 */
import { useState } from 'react'
import type { ReactNode } from 'react'

import { useI18n } from '../i18n'

/** How long the button reads "Copied" before returning to its label. */
const COPIED_MS = 2000

export function SecretReveal({
  title,
  warning,
  children,
  onDismiss,
}: {
  title: string
  /** Why this is the only time the value is visible. */
  warning: string
  children: ReactNode
  onDismiss?: () => void
}) {
  const { t } = useI18n()

  return (
    <div className="space-y-3 rounded-md border border-degraded/50 bg-degraded/10 p-3">
      <div className="flex items-start gap-3">
        <h3 className="text-sm font-semibold text-fg">{title}</h3>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="ml-auto rounded border border-border px-1.5 py-0.5 text-xs text-muted hover:text-fg"
          >
            {t('account.secretReveal.dismiss')}
          </button>
        )}
      </div>
      {/* Announced, not decorative: a screen-reader user who misses this loses
          the value with no way to get it back. */}
      <p role="alert" className="text-xs font-medium text-degraded">
        {warning}
      </p>
      {children}
    </div>
  )
}

/** One labelled, copyable, once-only value. */
export function SecretField({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint?: string
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted">{label}</span>
        <CopyButton label={label} value={value} />
      </div>
      {/* `break-all` because a 43-character token has no spaces to wrap at and
          would otherwise push the whole table sideways. */}
      <code className="block break-all rounded border border-border bg-bg px-2 py-1 font-mono text-xs text-fg">
        {value}
      </code>
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  )
}

export function CopyButton({ label, value }: { label: string; value: string }) {
  const { t } = useI18n()
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')

  async function copy() {
    try {
      // `navigator.clipboard` is undefined on insecure origins and can reject
      // outright when the permission is denied; both land here.
      const clipboard = navigator.clipboard
      if (!clipboard?.writeText) throw new Error('clipboard unavailable')
      await clipboard.writeText(value)
      setState('copied')
      setTimeout(() => setState('idle'), COPIED_MS)
    } catch {
      // Never silent: the value cannot be re-issued, so the user has to know
      // the copy did not happen while it is still on screen.
      setState('failed')
    }
  }

  return (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => void copy()}
        className="rounded border border-border px-1.5 py-0.5 text-xs text-muted hover:text-fg"
      >
        {t('account.secretReveal.copyValue', { label: label.toLowerCase() })}
      </button>
      {state === 'copied' && <span className="text-xs text-healthy">{t('common.copied')}</span>}
      {state === 'failed' && (
        <span className="text-xs text-down">{t('account.secretReveal.copyFailed')}</span>
      )}
    </span>
  )
}
